import { HealthProgramTrainingCompletion, TrainingId } from '@estercare/ester-shared'
import { ChartStep } from '../../components/charts/type'
import { theme } from '../../theme/theme'

export const COMPLETION_KEYS = {
  completed: 'completed',
  inProgress: 'inProgress',
  notStarted: 'notStarted',
} as const

// An ordered scale (genomförd -> pågående -> ej påbörjad) reads as one hue getting lighter, not
// three unrelated colours. Ink per step keeps the inline value legible on the fill.
export const getCompletionSteps = (t: (key: string) => string): ChartStep[] => [
  {
    key: COMPLETION_KEYS.completed,
    name: t('admin.statistics.completionCompleted'),
    color: theme.color.plumMid,
    ink: theme.color.white,
  },
  {
    key: COMPLETION_KEYS.inProgress,
    name: t('admin.statistics.completionInProgress'),
    color: theme.color.plumMidSoft,
    ink: theme.color.black,
  },
  {
    key: COMPLETION_KEYS.notStarted,
    name: t('admin.statistics.completionNotStarted'),
    color: theme.color.plumMidPale,
    ink: theme.color.black,
  },
]

// Same scale as getCompletionSteps, but in the plum/rose/beige tones used specifically for the
// competence section so it doesn't read as identical to the purple-toned charts elsewhere.
export const getCompetenceCompletionSteps = (t: (key: string) => string): ChartStep[] => [
  {
    key: COMPLETION_KEYS.completed,
    name: t('admin.statistics.completionCompleted'),
    color: theme.color.plum,
    ink: theme.color.white,
  },
  {
    key: COMPLETION_KEYS.inProgress,
    name: t('admin.statistics.completionInProgress'),
    color: theme.color.plumSoft,
    ink: theme.color.white,
  },
  {
    key: COMPLETION_KEYS.notStarted,
    name: t('admin.statistics.completionNotStarted'),
    color: theme.color.plumPale,
    ink: theme.color.black,
  },
]

// Which group each course is measured against — the same mapping the endpoint applies.
const ELIGIBLE_GROUP_KEYS: Partial<Record<TrainingId, string>> = {
  [TrainingId.WOMENS_HEALTH_TRAINING]: 'admin.statistics.eligibleCoworkers',
  [TrainingId.MANAGER_TRAINING]: 'admin.statistics.eligibleManagers',
}

export const getEligibleGroupKey = (trainingId: TrainingId) => ELIGIBLE_GROUP_KEYS[trainingId]

// Course titles come from Contentful so they read the same here as they do for the employee.
// These are only reached when the entry is missing or unpublished.
const FALLBACK_NAME_KEYS: Partial<Record<TrainingId, string>> = {
  [TrainingId.WOMENS_HEALTH_TRAINING]: 'admin.statistics.womensHealthTrainingName',
  [TrainingId.MANAGER_TRAINING]: 'admin.statistics.managerTrainingName',
}

export const getFallbackNameKey = (trainingId: TrainingId) => FALLBACK_NAME_KEYS[trainingId]

export const getCompletionValues = ({ eligible, completed, inProgress }: HealthProgramTrainingCompletion) => ({
  [COMPLETION_KEYS.completed]: completed,
  [COMPLETION_KEYS.inProgress]: inProgress,
  // Clamped: a user removed after completing a course would otherwise push this negative.
  [COMPLETION_KEYS.notStarted]: Math.max(eligible - completed - inProgress, 0),
})
